// Sestaví model „detailů okna" pro info bublinu (klik na typový chip P/M v hlavičce).
// Replikuje legacy Magic bublinu (WindowInfoBubble): titulek + identita (třída/URL/agenda)
// + velikost vnitřního obsahu + parametry. Funguje pro nativní (P) i legacy (M) okna:
//   - native: identita = agendaId, parametry = open/state params vrstvy
//   - legacy: identita = „Třída: X" / „Stránka: url" (chrome.info; fallback formGlobalName),
//             parametry = formParams
// Velikost (inner iframe/content) měří komponenta z DOM a předává jako `innerSize`.

import type { WindowChromeData } from '@/shared/legacyMagicBridge';
import type { WindowKind } from '@/shared/navigation/core';

import type { DesktopWindow } from '../core/deriveDesktopProjection';

export interface WindowInfoRow {
  readonly label: string;
  readonly value: string;
}

export interface WindowInfoModel {
  readonly title: string;
  readonly kind: WindowKind;
  /** Metadata okna (identita + velikost obsahu). */
  readonly meta: readonly WindowInfoRow[];
  /** Parametry, se kterými bylo okno otevřené. */
  readonly params: readonly WindowInfoRow[];
}

/** Naměřená velikost vnitřního obsahu okna (iframe u legacy / content u native). */
export interface WindowInnerSize {
  readonly w: number;
  readonly h: number;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** Identita okna jako řádek label/value. Legacy „Třída: X"/„Stránka: url" rozdělí na label+value. */
function identityRow(win: DesktopWindow, chrome: WindowChromeData | undefined): WindowInfoRow {
  if (win.kind !== 'legacy') {
    return { label: 'Agenda', value: win.agendaId };
  }
  const info = chrome?.info?.trim();
  if (info) {
    const sep = info.indexOf(': ');
    if (sep > 0) return { label: info.slice(0, sep), value: info.slice(sep + 2) };
    return { label: 'Formulář', value: info };
  }
  return { label: 'Formulář', value: formatValue(win.layer.openParams['formGlobalName']) };
}

/** Sestaví model detailů okna z projekce + (u legacy) vycucnutého chrome + naměřené velikosti. */
export function buildWindowInfo(
  win: DesktopWindow,
  chrome: WindowChromeData | undefined,
  innerSize?: WindowInnerSize
): WindowInfoModel {
  const isLegacy = win.kind === 'legacy';
  const title = isLegacy && chrome?.title ? chrome.title : win.title;

  const meta: WindowInfoRow[] = [identityRow(win, chrome)];
  if (innerSize) {
    meta.push({ label: 'Velikost', value: `${innerSize.w} × ${innerSize.h} px` });
  }

  const rawParams = isLegacy
    ? ((win.layer.openParams['formParams'] as Record<string, unknown> | undefined) ?? {})
    : { ...win.layer.openParams, ...win.layer.stateParams };
  const params: WindowInfoRow[] = Object.entries(rawParams).map(([label, value]) => ({
    label,
    value: formatValue(value),
  }));

  return { title, kind: win.kind, meta, params };
}
